import styled from "styled-components";
import { fontFamilies, fontSizes } from "components/shared/tokens/typography";
import red from "components/shared/tokens/color/red";

const DARK_NAVY = "#293142";
const MUTED = "#7d8599";
const PAGE_BACKGROUND = "#f5f6fb";

export const Page = styled.main`
  width: 100%;
  height: 100vh;
  height: 100dvh;
  display: flex;
  justify-content: center;
  overflow: hidden;
  background: ${PAGE_BACKGROUND};
  color: ${DARK_NAVY};
  font-family: ${fontFamilies.regular};
`;

export const PageShell = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: ${PAGE_BACKGROUND};
`;

export const Header = styled.header`
  min-height: 64px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: #ffffff;

  @media (max-width: 420px) {
    padding-right: 16px;
    padding-left: 16px;
  }
`;

export const HeaderTitle = styled.h1`
  margin: 0;
  color: ${DARK_NAVY};
  font-family: ${fontFamilies.semiBold};
  font-size: ${fontSizes.z400};
  line-height: 1.3;
`;

export const ActionButton = styled.button`
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }

  &:active {
    opacity: 0.7;
  }
`;

export const Content = styled.section`
  min-height: 0;
  overflow-y: auto;
  padding: 28px 24px 40px;

  @media (max-width: 420px) {
    padding: 24px 16px 32px;
  }
`;

export const UsersToolbar = styled.div`
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

export const UserCount = styled.h2`
  margin: 0;
  color: ${DARK_NAVY};
  font-family: ${fontFamilies.semiBold};
  font-size: ${fontSizes.z500};
  line-height: 1.25;
`;

export const AddUserButton = styled.button`
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 10px 14px;
  border: 0;
  border-radius: 11px;
  background: ${red.z500};
  color: #ffffff;
  font-family: ${fontFamilies.regular};
  font-size: ${fontSizes.z300};
  white-space: nowrap;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 2;
  }

  &:active {
    background: ${red.z600};
  }

  @media (max-width: 360px) {
    min-height: 40px;
    padding: 9px 12px;
    font-size: ${fontSizes.z200};
  }
`;

export const UserList = styled.div`
  overflow: hidden;
  padding: 8px 16px;
  border: 1px solid #f0f2f6;
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 4px 18px rgba(41, 49, 66, 0.06);

  @media (max-width: 420px) {
    padding: 8px 14px;
    border-radius: 20px;
  }
`;

export const UserListState = styled.div`
  min-height: 190px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 12px;
  text-align: center;

  strong {
    color: ${DARK_NAVY};
    font-family: ${fontFamilies.semiBold};
    font-size: ${fontSizes.z300};
  }

  p {
    max-width: 310px;
    margin: 7px 0 0;
    color: ${MUTED};
    font-size: ${fontSizes.z200};
    line-height: 1.45;
  }

  button {
    margin-top: 16px;
    padding: 9px 18px;
    border: 1px solid ${red.z500};
    border-radius: 9px;
    background: #ffffff;
    color: ${red.z500};
    font-family: ${fontFamilies.semiBold};
    font-size: ${fontSizes.z200};
    cursor: pointer;
  }

  .spinner {
    width: 28px;
    height: 28px;
    margin-bottom: 14px;
    border: 3px solid #f8dadd;
    border-top-color: ${red.z500};
    border-radius: 50%;
    animation: manage-users-spin 0.8s linear infinite;
  }

  @keyframes manage-users-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const UserCard = styled.article`
  position: relative;
  padding: 16px 0;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: 0;
    left: 58px;
    border-bottom: 1px dashed #e0e4ec;
  }
`;

export const UserIdentity = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  align-items: start;
  gap: 12px;
`;

export const Avatar = styled.div`
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: #f7f8fb;
  color: ${MUTED};
  font-family: ${fontFamilies.regular};
  font-size: ${fontSizes.z300};
  line-height: 1;

  @media (max-width: 420px) {
    width: 42px;
    height: 42px;
  }
`;

export const UserDetails = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const UserHeading = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

export const UserName = styled.h3`
  min-width: 0;
  margin: 0;
  color: #3a4357;
  font-family: ${fontFamilies.semiBold};
  font-size: ${fontSizes.z300};
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const roleTones = {
  blue: {
    background: "#dce8ff",
    color: "#2161c4",
  },
  green: {
    background: "#e4f3e9",
    color: "#267347",
  },
  orange: {
    background: "#fbd8b3",
    color: "#c85126",
  },
};

export const RoleBadge = styled.span`
  max-width: 100%;
  padding: 3px 6px;
  border-radius: 6px;
  background: ${({ $tone }) => (roleTones[$tone] || roleTones.blue).background};
  color: ${({ $tone }) => (roleTones[$tone] || roleTones.blue).color};
  font-family: ${fontFamilies.semiBold};
  font-size: ${fontSizes.z100};
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

export const ContactList = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ContactItem = styled.div`
  min-width: 0;
  color: ${MUTED};
  font-size: ${fontSizes.z200};
  line-height: 1.35;
  overflow-wrap: anywhere;
`;
